/**
 * Premiere-importable FCP 7 XML (xmeml v4) for image-based subtitle stills.
 * Each PNG is a still clip placed at the event in/out on a sequence track.
 */

const NTSC_RATES = {
    23.976: { timebase: 24, ntsc: true },
    23.98: { timebase: 24, ntsc: true },
    29.97: { timebase: 30, ntsc: true },
    59.94: { timebase: 60, ntsc: true },
};

export function getXmemlRate(frameRate) {
    const fps = parseFloat(frameRate);
    if (NTSC_RATES[fps]) {
        return { ...NTSC_RATES[fps] };
    }
    const timebase = Math.round(fps) || 30;
    return { timebase, ntsc: false };
}

export function secondsToFrames(sec, frameRate) {
    const fps = parseFloat(frameRate);
    let num;
    let den;
    if (fps === 23.976 || fps === 23.98) {
        num = 24000;
        den = 1001;
    } else if (fps === 29.97) {
        num = 30000;
        den = 1001;
    } else if (fps === 59.94) {
        num = 60000;
        den = 1001;
    } else {
        num = fps;
        den = 1;
    }
    let frameCount = Math.max(0, Math.round(parseFloat(sec) * num / den));
    if (!Number.isFinite(frameCount)) {
        frameCount = 0;
    }
    return frameCount;
}

export function getFcpXmlFileName(scriptName) {
    const name = String(scriptName || "untitled.xml").trim() || "untitled.xml";
    const lastSep = Math.max(name.lastIndexOf("/"), name.lastIndexOf("\\"));
    const lastDot = name.lastIndexOf(".");
    const stem = lastDot > lastSep ? name.slice(0, lastDot) : name;
    return `${stem || "untitled"}_fcp.xml`;
}

export function toPremierePathUrl(absolutePath) {
    const posix = String(absolutePath || "").replace(/\\/g, "/");
    const windowsMatch = posix.match(/^\/?([A-Za-z]):(\/.*)?$/);
    let pathname;
    if (windowsMatch) {
        const rest = windowsMatch[2] || "/";
        pathname = `/${windowsMatch[1]}%3a${encodePathSegments(rest)}`;
    } else {
        const withSlash = posix.startsWith("/") ? posix : `/${posix}`;
        pathname = encodePathSegments(withSlash);
        if (!pathname.startsWith("/")) {
            pathname = `/${pathname}`;
        }
    }
    return `file://localhost${pathname}`;
}

export function getImageFileName(imagePrefix, index, options = {}) {
    const extension = options.extension || "png";
    const separator = options.separator ?? "";
    return `${imagePrefix}${separator}${String(index + 1).padStart(4, "0")}.${extension}`;
}

function encodePathSegments(pathWithSlashes) {
    return pathWithSlashes
        .split("/")
        .map((segment, i) => (i === 0 ? segment : encodeURIComponent(segment)))
        .join("/");
}

function escapeXml(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
}

function joinFolderFile(folder, fileName) {
    const folderStr = String(folder || "");
    if (!folderStr) {
        return fileName;
    }
    if (/[\\/]$/.test(folderStr)) {
        return folderStr + fileName;
    }
    const sep = folderStr.includes("\\") ? "\\" : "/";
    return `${folderStr}${sep}${fileName}`;
}

function getPixelAspect(resolutionId) {
    if (resolutionId === "ntsc_sd") {
        return "NTSC-601";
    }
    if (resolutionId === "pal_sd") {
        return "PAL-601";
    }
    return "square";
}

function getFieldDominance(resolutionId) {
    if (resolutionId === "ntsc_sd") {
        return "lower";
    }
    if (resolutionId === "pal_sd" || resolutionId === "hd_1080i") {
        return "upper";
    }
    return "none";
}

function rateXml(timebase, ntsc, indent) {
    return `${indent}<rate>
${indent}\t<timebase>${timebase}</timebase>
${indent}\t<ntsc>${ntsc ? "TRUE" : "FALSE"}</ntsc>
${indent}</rate>`;
}

function packTracks(clips) {
    const trackEnds = [];
    for (const clip of clips) {
        let track = 0;
        while (track < trackEnds.length && clip.start < trackEnds[track]) {
            track += 1;
        }
        clip.track = track;
        trackEnds[track] = clip.end;
    }
    return trackEnds.length || 1;
}

function buildClipXml(clip, timebase, ntsc, alphatype, pixelAspect) {
    const name = escapeXml(clip.fileName);
    return `\t\t\t\t\t<clipitem id="clipitem-${clip.id}">
\t\t\t\t\t\t<name>${name}</name>
\t\t\t\t\t\t<enabled>TRUE</enabled>
\t\t\t\t\t\t<duration>${clip.duration}</duration>
${rateXml(timebase, ntsc, "\t\t\t\t\t\t")}
\t\t\t\t\t\t<start>${clip.start}</start>
\t\t\t\t\t\t<end>${clip.end}</end>
\t\t\t\t\t\t<in>0</in>
\t\t\t\t\t\t<out>${clip.duration}</out>
\t\t\t\t\t\t<stillframe>TRUE</stillframe>
\t\t\t\t\t\t<alphatype>${alphatype}</alphatype>
\t\t\t\t\t\t<pixelaspectratio>${pixelAspect}</pixelaspectratio>
\t\t\t\t\t\t<anamorphic>FALSE</anamorphic>
\t\t\t\t\t\t<file id="file-${clip.id}">
\t\t\t\t\t\t\t<name>${name}</name>
\t\t\t\t\t\t\t<pathurl>${escapeXml(clip.pathUrl)}</pathurl>
${rateXml(timebase, ntsc, "\t\t\t\t\t\t\t")}
\t\t\t\t\t\t\t<duration>${clip.duration}</duration>
\t\t\t\t\t\t\t<media>
\t\t\t\t\t\t\t\t<video>
\t\t\t\t\t\t\t\t\t<duration>${clip.duration}</duration>
\t\t\t\t\t\t\t\t\t<stillframe>TRUE</stillframe>
\t\t\t\t\t\t\t\t\t<samplecharacteristics>
\t\t\t\t\t\t\t\t\t\t<width>${clip.width}</width>
\t\t\t\t\t\t\t\t\t\t<height>${clip.height}</height>
\t\t\t\t\t\t\t\t\t</samplecharacteristics>
\t\t\t\t\t\t\t\t</video>
\t\t\t\t\t\t\t</media>
\t\t\t\t\t\t</file>
\t\t\t\t\t</clipitem>`;
}

/**
 * @param {object} options
 * @param {Array<{start: number, end: number}>} options.events
 * @param {{width: number, height: number}} options.resolution
 * @param {string} options.targetFolderPath
 * @param {string} options.imagePrefix
 * @param {string} options.sequenceName
 * @param {number|string} options.frameRate
 * @param {boolean} options.dropFrame
 * @param {boolean} options.useTransparentBackground
 * @param {string} [options.resolutionId]
 * @param {string} [options.imageExtension]
 * @param {string} [options.imageSeparator]
 */
export function generateFcpXml({
    events = [],
    resolution,
    targetFolderPath,
    imagePrefix,
    sequenceName,
    frameRate,
    dropFrame,
    useTransparentBackground,
    resolutionId,
    imageExtension = "png",
    imageSeparator = "",
}) {
    const { timebase, ntsc } = getXmemlRate(frameRate);
    const width = resolution?.width || 1920;
    const height = resolution?.height || 1080;
    const alphatype = useTransparentBackground ? "straight" : "none";
    const colordepth = useTransparentBackground ? 32 : 24;
    const pixelAspect = getPixelAspect(resolutionId);
    const fieldDominance = getFieldDominance(resolutionId);
    const displayformat = dropFrame && (parseFloat(frameRate) === 29.97 || parseFloat(frameRate) === 59.94)
        ? "DF"
        : "NDF";
    const tcString = displayformat === "DF" ? "00;00;00;00" : "00:00:00:00";

    const clips = events.map((event, index) => {
        const start = secondsToFrames(event.start, frameRate);
        let end = secondsToFrames(event.end, frameRate);
        if (end <= start) {
            end = start + 1;
        }
        const fileName = getImageFileName(imagePrefix, index, {
            extension: imageExtension,
            separator: imageSeparator,
        });
        return {
            id: index + 1,
            fileName,
            pathUrl: toPremierePathUrl(joinFolderFile(targetFolderPath, fileName)),
            start,
            end,
            duration: end - start,
            width,
            height,
            track: 0,
        };
    });

    const trackCount = packTracks(clips);
    const sequenceDuration = clips.reduce(
        (max, clip) => Math.max(max, clip.end),
        0,
    );
    const name = escapeXml(sequenceName || "Subtitles");

    const tracksXml = [];
    for (let track = 0; track < trackCount; track += 1) {
        const trackClips = clips.filter((clip) => clip.track === track);
        const clipXml = trackClips
            .map((clip) => buildClipXml(clip, timebase, ntsc, alphatype, pixelAspect))
            .join("\n");
        tracksXml.push(`\t\t\t\t<track>
\t\t\t\t\t<enabled>TRUE</enabled>
\t\t\t\t\t<locked>FALSE</locked>
${clipXml}
\t\t\t\t</track>`);
    }

    return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE xmeml>
<xmeml version="4">
	<sequence id="sequence-1">
		<name>${name}</name>
		<duration>${sequenceDuration}</duration>
${rateXml(timebase, ntsc, "\t\t")}
		<timecode>
${rateXml(timebase, ntsc, "\t\t\t")}
			<string>${tcString}</string>
			<frame>0</frame>
			<displayformat>${displayformat}</displayformat>
		</timecode>
		<media>
			<video>
				<format>
					<samplecharacteristics>
${rateXml(timebase, ntsc, "\t\t\t\t\t\t")}
						<width>${width}</width>
						<height>${height}</height>
						<anamorphic>FALSE</anamorphic>
						<pixelaspectratio>${pixelAspect}</pixelaspectratio>
						<fielddominance>${fieldDominance}</fielddominance>
						<colordepth>${colordepth}</colordepth>
					</samplecharacteristics>
				</format>
${tracksXml.join("\n")}
			</video>
		</media>
	</sequence>
</xmeml>
`;
}
